import i18next from "i18next";
import { initReactI18next } from "react-i18next";

const resources = {
  "zh-CN": {
    translation: {
      productName: "Toy Console",
      productDescription: "统一管理 13 个 Toy App 的治理状态与平台能力",
      nav: {
        overview: "总览",
        market: "应用市场",
        apps: "App 管理",
        users: "用户",
        entitlements: "权益",
        operations: "任务与 Provider",
        audit: "审计日志",
        settings: "设置",
      },
      navGroups: {
        platform: "平台管理（Toy）",
        app: "应用管理（App）",
      },
      common: {
        edit: "编辑",
        invalidUserId: "请输入有效的账号 UUID。",
        thinking: "思考中...",
        retry: "重试",
        refresh: "刷新",
        save: "保存",
        cancel: "取消",
        confirm: "确认执行",
        reason: "操作原因",
        noData: "暂无数据",
        unavailable: "不可用",
        signIn: "使用管理员账号登录",
        signOut: "退出登录",
        previous: "上一页",
        next: "下一页",
        page: "第 {{current}} / {{total}} 页",
        revisionConflict: "数据已发生变化，请刷新后重试。",
        requestFailed: "请求失败，请稍后重试。",
        accessDenied: "当前账号无权访问 Toy Console。",
        strongAuthRequired: "需要完成强认证后才能进入管理端。",
        appCount: "13 个 App",
        languageChinese: "简体中文",
        languageEnglish: "English",
        navigation: "管理模块",
      },
      status: {
        healthy: "健康",
        warning: "警告",
        failure: "异常",
        active: "正常",
        banned: "已停用",
        suspended: "已暂停",
        retired: "已退役",
        available: "已接入",
        degraded: "降级",
        unavailable: "不可用",
        not_integrated: "未接入",
        not_applicable: "不适用",
        succeeded: "成功",
        failed: "失败",
        denied: "已拒绝",
        configured: "已配置",
        unconfigured: "未配置",
        disabled: "未启用",
        enabled: "已启用",
        unknown: "未知",
        archived: "已归档",
        expired: "已过期",
        featured: "精选",
        boolean: "开关",
        limit: "数量上限",
      },
      overview: {
        title: "平台总览",
        subtitle: "所有指标均来自 PostgreSQL 与真实运行状态。",
        users: "用户总数",
        activeUsers: "活跃用户",
        visibleApps: "可见 App",
        unavailableCapabilities: "未接入或异常能力",
        activeEntitlements: "有效权益",
        deadLetters: "死信任务",
        providers: "健康 Provider",
        audits: "近 24 小时审计事件",
      },
      apps: {
        revision: "版本",
        isoDatePlaceholder: "例如 2026-12-31T00:00:00Z",
        localDatePlaceholder: "YYYY-MM-DDTHH:mm（本地时间）",
        invalidRuleNumber: "灰度比例须在 0 至 100 之间，优先级须为整数。",
        title: "App 管理",
        subtitle: "管理各 App 的专属治理能力与 Feature Flag。",
        app: "App",
        status: "状态",
        governance: "治理状态",
        releaseCount: "发布版本数",
        capabilities: "专属能力",
        flags: "Feature Flag",
        restrictedData: "数据边界",
        newFlag: "新建 Flag",
        flagOwner: "负责人账号 UUID",
        flagOwnerPlaceholder: "可留空；填写实际平台账号 UUID",
        flagExpiration: "到期时间（ISO 8601，可留空）",
        expiredFlag: "已到期",
        flagRevision: "当前 revision {current}，保存后 revision {next}",
        flagImpact:
          "影响 App：{app}；既有规则 {rules} 条。实际生效取决于客户端消费点及规则范围。到期标记用于治理提醒。",
        diffField: "字段",
        diffBefore: "当前版本",
        diffAfter: "待保存版本",
        flagHistory: "定义版本历史",
        flagRestore: "载入为待保存版本",
        flagRestoreHint:
          "从不可变审计记录载入历史定义，保留当前规则。核对字段差异、填写原因并确认后，按当前 revision 保存为新版本。",
        policyRestoreHint:
          "载入历史配置草稿，核对差异、填写原因并确认后，按当前 revision 保存为新版本。",
        effectiveTitle: "当前账户生效诊断",
        effectiveHint:
          "按所选平台与应用版本计算当前管理账户的有效配置。其他账户的 cohort 和稳定分桶结果可能不同；运行请求失败原因显示在下方。",
        effectiveCatalogRevision:
          "目录 revision {{revision}} · 计算时间 {{time}}",
        effectiveSource: "命中来源",
        effectiveDefinition: "定义 revision",
        effectiveRule: "规则 revision",
        flagKey: "Flag Key",
        valueType: "值类型",
        defaultValue: "默认值",
        descriptionKey: "说明文案 Key",
        flagReason: "说明本次 Feature Flag 变更原因",
        saveFlag: "保存 Feature Flag",
        rules: "生效规则",
        newRule: "新建规则",
        ruleEnabled: "启用规则",
        ruleScope: "生效范围",
        ruleScope_global: "全局",
        ruleScope_platform: "指定平台",
        "ruleScope_app-version": "指定 App 版本",
        ruleScope_percentage: "灰度百分比",
        ruleScope_cohort: "内部使用组",
        ruleScopeValue: "范围值",
        rulePercentage: "灰度百分比",
        rulePercentagePresets: "推荐灰度梯度",
        rulePriority: "优先级",
        ruleStartsAt: "开始时间",
        ruleEndsAt: "结束时间",
        ruleImpact: "影响预览",
        ruleImpactGlobal: "所有平台和版本",
        ruleImpactPlatform: "平台 {{target}}",
        ruleImpactVersion: "App 版本 {{target}}",
        ruleImpactPercentage: "稳定分桶后的 {{percentage}}% 用户",
        ruleImpactCohort: "{{cohort}}中的用户",
        ruleReason: "说明本次生效规则变更原因",
        saveRule: "保存规则",
        available: "已接入",
        degraded: "降级",
        unavailable: "不可用",
        not_integrated: "未接入",
        not_applicable: "不适用",
      },
      market: {
        title: "应用市场",
        invalidSortOrder: "市场排序须为非负整数。",
        subtitle: "管理 Toy 应用市场中的精选、排序与平台可见性。",
        app: "市场 App",
        visibility: "市场可见",
        featured: "设为精选",
        sortOrder: "市场排序",
        notes: "平台备注",
        savePolicy: "保存市场策略",
        policyReason: "说明本次市场策略变更原因",
      },
      stewardRooms: {
        title: "房间授权",
        explanation:
          "核对用户与房间的实际关系后授予权限。撤销将停止该用户的自动采集与提醒。",
        roomId: "房间编号",
        role: "角色",
        viewer: "查看者",
        owner: "所有者",
        grant: "授予或更新",
        revoke: "撤销授权",
        invalid: "请填写有效房间编号、操作原因并确认。",
      },
      users: {
        title: "用户管理",
        subtitle: "仅展示账户元数据，不读取用户 App 内容。",
        search: "按 ID、用户名、显示名或邮箱搜索",
        user: "用户",
        status: "状态",
        entitlements: "有效权益",
        rolloutCohorts: "提前使用组",
        createdAt: "创建时间",
        actions: "操作",
        suspend: "停用账号",
        restore: "恢复账号",
        revokeSessions: "撤销会话",
        changeReason: "填写本次用户操作原因",
        saveRolloutCohorts: "保存提前使用组",
      },
      rolloutCohorts: {
        development: "开发人员",
        testing: "测试人员",
        internal: "内部员工",
      },
      entitlements: {
        title: "权益管理",
        startsAt: "生效日期",
        expiresAt: "到期日期（可留空）",
        datePlaceholder: "YYYY-MM-DD",
        invalidPeriod: "请输入有效日期，到期日期须晚于或等于生效日期。",
        subtitle:
          "管理用户授权与各付费级别的功能清单；所有写入均执行版本校验和审计。",
        assignments: "用户授权",
        tierFeatures: "Tier 功能清单",
        id: "权益 ID",
        user: "用户 ID",
        app: "App",
        tier: "等级",
        period: "有效期",
        status: "状态",
      },
      tierFeatures: {
        invalidLimit: "数量上限须为非负整数。",
        app: "App",
        tier: "查看级别",
        free: "免费版（Free）",
        plus: "Plus",
        pro: "Pro",
        features: "功能清单",
        unlimited: "不限量",
        monotonicHint:
          "Free 是只读产品基线；Plus 必须包含 Free，Pro 必须包含 Plus。保存后策略会随签名权益令牌下发。",
        reason: "说明本次 Tier 功能策略变更原因",
        save: "保存并下发",
      },
      operations: {
        title: "任务与 Provider",
        subtitle: "查看死信队列和外部 Provider 的非敏感运行摘要。",
        deadLetters: "死信队列",
        source: "来源",
        count: "数量",
        replay: "重放",
        replayReason: "填写重放任务的原因",
        providers: "Provider 状态",
        provider: "Provider",
        configuration: "配置状态",
        lastChecked: "最近检查",
      },
      audit: {
        title: "审计日志",
        app: "App 标识",
        allResults: "全部结果",
        pagination: "审计分页",
        subtitle: "不可变的管理员操作和拒绝访问记录。",
        time: "时间",
        actor: "操作者",
        action: "动作",
        target: "目标",
        result: "结果",
        reason: "原因",
      },
      settings: {
        title: "设置",
        subtitle: "查看当前 Admin 的数据驱动授权范围和认证状态。",
        owner: "Admin 用户 ID",
        grants: "角色与作用域绑定",
        strongAuth: "强认证",
        authenticatedAt: "认证时间",
        permissions: "权限",
        enabled: "已启用",
        disabled: "未启用",
        privacyTitle: "隐私边界",
        privacyBody:
          "Console 只管理平台元数据、授权和运行状态，不读取 App 用户内容、第三方明文凭据或密钥。",
      },
    },
  },
  "en-US": {
    translation: {
      productName: "Toy Console",
      productDescription: "Governance and operations for all 13 Toy apps",
      nav: {
        overview: "Overview",
        market: "App Market",
        apps: "Apps",
        users: "Users",
        entitlements: "Entitlements",
        operations: "Jobs & Providers",
        audit: "Audit",
        settings: "Settings",
      },
      navGroups: {
        platform: "Platform management (Toy)",
        app: "App management",
      },
      common: {
        thinking: "Thinking...",
        edit: "Edit",
        invalidUserId: "Enter a valid account UUID.",
        retry: "Retry",
        refresh: "Refresh",
        save: "Save",
        cancel: "Cancel",
        confirm: "Confirm",
        reason: "Reason",
        noData: "No data",
        unavailable: "Unavailable",
        signIn: "Sign in with the administrator account",
        signOut: "Sign out",
        previous: "Previous",
        next: "Next",
        page: "Page {{current}} of {{total}}",
        revisionConflict: "The resource changed. Refresh and try again.",
        requestFailed: "The request failed. Try again later.",
        accessDenied: "This account cannot access Toy Console.",
        appCount: "13 Apps",
        languageChinese: "简体中文",
        languageEnglish: "English",
        navigation: "Management area",
      },
      status: {
        healthy: "Healthy",
        warning: "Warning",
        failure: "Failure",
        active: "Active",
        banned: "Suspended",
        suspended: "Suspended",
        retired: "Retired",
        available: "Available",
        degraded: "Degraded",
        unavailable: "Unavailable",
        not_integrated: "Not integrated",
        not_applicable: "Not applicable",
        succeeded: "Succeeded",
        failed: "Failed",
        denied: "Denied",
        configured: "Configured",
        unconfigured: "Unconfigured",
        disabled: "Disabled",
        enabled: "Enabled",
        unknown: "Unknown",
        archived: "Archived",
        expired: "Expired",
        featured: "Featured",
        boolean: "Toggle",
        limit: "Limit",
      },
      overview: {
        title: "Platform overview",
        subtitle: "Metrics come from PostgreSQL and live runtime state.",
        users: "Users",
        activeUsers: "Active users",
        visibleApps: "Visible apps",
        unavailableCapabilities: "Unavailable capabilities",
        activeEntitlements: "Active entitlements",
        deadLetters: "Dead-letter jobs",
        providers: "Healthy providers",
        audits: "Audit events (24h)",
      },
      apps: {
        title: "App management",
        revision: "Revision",
        isoDatePlaceholder: "For example, 2026-12-31T00:00:00Z",
        localDatePlaceholder: "YYYY-MM-DDTHH:mm (local time)",
        invalidRuleNumber:
          "Rollout percentage must be between 0 and 100; priority must be an integer.",
        subtitle: "Manage app-specific capabilities and feature flags.",
        app: "App",
        status: "Status",
        governance: "Governance",
        releaseCount: "Releases",
        capabilities: "Capabilities",
        flags: "Feature flags",
        restrictedData: "Data boundary",
        newFlag: "New flag",
        flagOwner: "Owner account UUID",
        flagOwnerPlaceholder: "Optional existing platform account UUID",
        flagExpiration: "Expiration (ISO 8601, optional)",
        expiredFlag: "Expired",
        flagRevision: "Current revision {current}; next revision {next}",
        flagImpact:
          "Affected app: {app}; {rules} existing rules. Runtime effects depend on consuming clients and rule scope. Expiration is a governance reminder.",
        diffField: "Field",
        diffBefore: "Current version",
        diffAfter: "Pending version",
        flagHistory: "Definition version history",
        flagRestore: "Load as pending version",
        flagRestoreHint:
          "Load a historical definition from immutable audit records while keeping current rules. Review the differences, enter a reason and confirm to save a new version against the current revision.",
        policyRestoreHint:
          "Load a historical configuration draft. Review differences, enter a reason and confirm to save a new version against the current revision.",
        effectiveTitle: "Effective configuration diagnostics",
        effectiveHint:
          "Evaluate the current administrator account for the selected platform and app version. Cohorts and stable buckets can differ for other accounts. Request failure details appear below.",
        effectiveCatalogRevision:
          "Catalog revision {{revision}} · Evaluated at {{time}}",
        effectiveSource: "Matching source",
        effectiveDefinition: "Definition revision",
        effectiveRule: "Rule revision",
        flagKey: "Flag key",
        valueType: "Value type",
        defaultValue: "Default value",
        descriptionKey: "Description key",
        flagReason: "Explain this feature flag change",
        saveFlag: "Save feature flag",
        rules: "Effective rules",
        newRule: "New rule",
        ruleEnabled: "Enable rule",
        ruleScope: "Scope",
        ruleScope_global: "Global",
        ruleScope_platform: "Platform",
        "ruleScope_app-version": "App version",
        ruleScope_percentage: "Percentage rollout",
        ruleScope_cohort: "Internal cohort",
        ruleScopeValue: "Scope value",
        rulePercentage: "Rollout percentage",
        rulePercentagePresets: "Recommended rollout stages",
        rulePriority: "Priority",
        ruleStartsAt: "Starts at",
        ruleEndsAt: "Ends at",
        ruleImpact: "Impact preview",
        ruleImpactGlobal: "All platforms and versions",
        ruleImpactPlatform: "Platform {{target}}",
        ruleImpactVersion: "App version {{target}}",
        ruleImpactPercentage: "{{percentage}}% of stable subject buckets",
        ruleImpactCohort: "Users in {{cohort}}",
        ruleReason: "Explain this effective rule change",
        saveRule: "Save rule",
        available: "Available",
        degraded: "Degraded",
        unavailable: "Unavailable",
        not_integrated: "Not integrated",
        not_applicable: "Not applicable",
      },
      market: {
        title: "App Market",
        invalidSortOrder: "Market order must be a non-negative integer.",
        subtitle:
          "Manage featured placement, ordering, and platform visibility in the Toy app market.",
        app: "Market app",
        visibility: "Market visibility",
        featured: "Featured",
        sortOrder: "Market order",
        notes: "Platform notes",
        savePolicy: "Save market policy",
        policyReason: "Explain this market policy change",
      },
      stewardRooms: {
        title: "Room access",
        explanation:
          "Verify the user’s relationship to the room before granting access. Revocation stops collection and reminders for this user.",
        roomId: "Room ID",
        role: "Role",
        viewer: "Viewer",
        owner: "Owner",
        grant: "Grant or update",
        revoke: "Revoke access",
        invalid:
          "Enter a valid room ID, an operation reason, and confirmation.",
      },
      users: {
        title: "User management",
        subtitle: "Account metadata only; app content is never read.",
        search: "Search by ID, username, display name, or email",
        user: "User",
        status: "Status",
        entitlements: "Entitlements",
        rolloutCohorts: "Early-access cohorts",
        createdAt: "Created",
        actions: "Actions",
        suspend: "Suspend",
        restore: "Restore",
        revokeSessions: "Revoke sessions",
        changeReason: "Explain this user operation",
        saveRolloutCohorts: "Save early-access cohorts",
      },
      rolloutCohorts: {
        development: "Developers",
        testing: "Testers",
        internal: "Internal employees",
      },
      entitlements: {
        title: "Entitlements",
        startsAt: "Start date",
        expiresAt: "Expiry date (optional)",
        datePlaceholder: "YYYY-MM-DD",
        invalidPeriod:
          "Enter valid dates. Expiry must be on or after the start date.",
        subtitle:
          "Manage account grants and paid-tier feature lists with revision checks and audit.",
        assignments: "Account grants",
        tierFeatures: "Tier features",
        id: "Entitlement ID",
        user: "User ID",
        app: "App",
        tier: "Tier",
        period: "Period",
        status: "Status",
      },
      tierFeatures: {
        app: "App",
        tier: "View tier",
        invalidLimit: "Limits must be non-negative integers.",
        free: "Free",
        plus: "Plus",
        pro: "Pro",
        features: "Feature list",
        unlimited: "Unlimited",
        monotonicHint:
          "Free is a read-only product baseline. Plus must include Free, and Pro must include Plus. Saved policies are delivered in signed entitlement tokens.",
        reason: "Explain this tier feature policy change",
        save: "Save and distribute",
      },
      operations: {
        title: "Jobs & providers",
        subtitle:
          "Dead-letter queues and non-sensitive provider health summaries.",
        deadLetters: "Dead letters",
        source: "Source",
        count: "Count",
        replay: "Replay",
        replayReason: "Explain why these jobs should be replayed",
        providers: "Providers",
        provider: "Provider",
        configuration: "Configuration",
        lastChecked: "Last checked",
      },
      audit: {
        title: "Audit log",
        app: "App ID",
        allResults: "All results",
        pagination: "Audit pagination",
        subtitle: "Immutable administrator actions and denied access events.",
        time: "Time",
        actor: "Actor",
        action: "Action",
        target: "Target",
        result: "Result",
        reason: "Reason",
      },
      settings: {
        title: "Settings",
        subtitle:
          "Data-driven authorization grants and authentication state for the current Admin.",
        owner: "Admin user ID",
        grants: "Role and scope grants",
        strongAuth: "Strong authentication",
        authenticatedAt: "Authenticated at",
        permissions: "Permissions",
        enabled: "Enabled",
        disabled: "Disabled",
        privacyTitle: "Privacy boundary",
        privacyBody:
          "Console manages platform metadata, authorization, and operations only. It never reads app user content, plaintext third-party credentials, or secrets.",
      },
    },
  },
} as const;

await i18next.use(initReactI18next).init({
  resources,
  lng: navigator.language.startsWith("zh") ? "zh-CN" : "en-US",
  fallbackLng: "en-US",
  interpolation: { escapeValue: false },
});

export { i18next };
